import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { RefreshCw, FileText, X, Truck } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import ProjectSelect from '../../components/ProjectSelect';

// Packing → Sale Challan report. Packing is made first; a box is "used" once it is
// ticked on a Sale Challan line (Box Details). Items = boxes grouped by project +
// Proto Code. Pending = packed but not yet on any (non-cancelled) challan.

const ITEM_STATUS = {
  pending: { label: 'Pending',             cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  partial: { label: 'Partially Challaned', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  full:    { label: 'Fully Challaned',     cls: 'bg-green-50 text-green-700 border-green-200' },
};
const BOX_STATUS = {
  used:    { label: 'On Challan', cls: 'bg-green-50 text-green-700 border-green-200' },
  pending: { label: 'Pending',    cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};
const STATUS_FILTERS = {
  items: [['', 'All'], ['pending', 'Pending'], ['partial', 'Partially Challaned'], ['full', 'Fully Challaned']],
  boxes: [['', 'All'], ['pending', 'Pending'], ['used', 'On Challan']],
  vehicles: [['', 'All']],
};
const emptyFilters = { date_from: '', date_to: '', challan: '', vehicle: '', project_id: '', item: '', status: '' };
const vehicleText = c => [c.vehicle_number, c.vehicle_length].filter(Boolean).join(' · ');
const esc = (v) => String(v == null || v === '' ? '-' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const Badge = ({ meta }) => <span className={`inline-block whitespace-nowrap text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>;

export default function PackingChallanReport({ projects = [] }) {
  const { settings, formatDate } = useSettings();
  const [view, setView] = useState('items');          // 'items' | 'boxes' | 'vehicles'
  const [filters, setFilters] = useState(emptyFilters);
  const [data, setData] = useState({ summary: null, items: [], boxes: [], vehicles: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const res = await api.get('/packing/challan-report', { params });
      setData({ summary: res.data.summary || null, items: res.data.items || [], boxes: res.data.boxes || [], vehicles: res.data.vehicles || [] });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, []);

  // Text filters wait for typing to pause; the rest apply at once
  useEffect(() => {
    const t = setTimeout(() => load(filters), 350);
    return () => clearTimeout(t);
  }, [filters, load]);

  const set = (p) => setFilters(f => ({ ...f, ...p }));
  const switchView = (v) => {
    setView(v);
    // A status only meaningful in the other view is cleared
    if (filters.status && !STATUS_FILTERS[v].some(([k]) => k === filters.status)) set({ status: '' });
  };
  const hasFilters = Object.values(filters).some(v => v !== '');
  const { summary, items, boxes, vehicles } = data;
  const rowsFor = v => (v === 'items' ? items : v === 'boxes' ? boxes : vehicles);

  const exportPDF = () => {
    const rows = rowsFor(view);
    if (!rows.length) return toast.error('Nothing to export');
    const now = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const th = 'border:1px solid #999;padding:4px;font-size:8.5pt;background:#F2F2F2;text-align:center;';
    const td = 'border:1px solid #999;padding:4px;font-size:8.5pt;vertical-align:middle;';
    const proj = r => [r.proj_code, r.project_name].filter(Boolean).join(' — ');
    const applied = [
      filters.date_from && `Packed from ${filters.date_from}`, filters.date_to && `to ${filters.date_to}`,
      filters.challan && `Challan: ${filters.challan}`,
      filters.vehicle && `Vehicle: ${filters.vehicle}`,
      filters.project_id && `Project: ${(projects.find(p => String(p.id) === String(filters.project_id)) || {}).name || ''}`,
      filters.item && `Item: ${filters.item}`,
      filters.status && `Status: ${(STATUS_FILTERS[view].find(([k]) => k === filters.status) || [])[1]}`,
    ].filter(Boolean).join(' | ');
    const head = view === 'items'
      ? ['PROJECT', 'PROTO CODE', 'ITEM', 'BOXES', 'ON CHALLAN', 'PENDING', 'CHALLAN QTY', 'CHALLANS', 'STATUS']
      : view === 'boxes'
      ? ['BOX NO', 'PROJECT', 'PROTO CODE', 'ITEM', 'CONTENTS', 'PACKED ON', 'CHALLAN', 'VEHICLE', 'STATUS']
      : ['VEHICLE NO', 'LENGTH', 'TRANSPORT', 'TRIPS', 'CHALLANS', 'BOXES', 'LABELS', 'ITEMS', 'PROJECTS'];
    const body = view === 'vehicles'
      ? vehicles.map(v => `<tr><td style="${td}font-weight:700;">${esc(v.vehicle_number || 'No vehicle no.')}</td><td style="${td}">${esc(v.vehicle_lengths.join(', '))}</td>
          <td style="${td}">${esc(v.transports.join(', '))}</td><td style="${td}text-align:center;">${v.trips}</td>
          <td style="${td}">${esc(v.challans.map(c => `${c.challan_number} (${formatDate(c.challan_date)}${c.client_name ? ', ' + c.client_name : ''}) ×${c.boxes}`).join('; '))}</td>
          <td style="${td}text-align:center;font-weight:700;">${v.boxes}</td><td style="${td}text-align:center;">${v.labels}</td>
          <td style="${td}">${esc(v.items.join(', '))}</td><td style="${td}">${esc(v.projects.join(', '))}</td></tr>`).join('')
      : view === 'items'
      ? items.map(r => `<tr><td style="${td}">${esc(proj(r))}</td><td style="${td}font-family:monospace;">${esc(r.proto_code)}</td><td style="${td}">${esc(r.item_name)}</td>
          <td style="${td}text-align:center;">${r.boxes}${r.labels !== r.boxes ? ` (${r.labels} labels)` : ''}</td><td style="${td}text-align:center;">${r.used_boxes}</td>
          <td style="${td}text-align:center;font-weight:700;">${r.pending_boxes}</td><td style="${td}text-align:center;">${r.challan_qty || '-'}</td>
          <td style="${td}">${esc(r.challans.join(', '))}</td><td style="${td}">${esc(ITEM_STATUS[r.status].label)}</td></tr>`).join('')
      : boxes.map(b => `<tr><td style="${td}font-weight:700;">${esc(b.box_number)}${b.labels > 1 ? ` (${b.labels})` : ''}</td><td style="${td}">${esc(proj(b))}</td>
          <td style="${td}font-family:monospace;">${esc(b.proto_code)}</td><td style="${td}">${esc(b.main_item)}</td>
          <td style="${td}">${esc(b.contents.map(c => `${c.item_name} ×${c.quantity}`).join(', '))}</td><td style="${td}">${esc(formatDate(b.packed_at))}</td>
          <td style="${td}">${esc(b.challans.map(c => c.challan_number).join(', '))}</td><td style="${td}">${esc(b.challans.map(vehicleText).filter(Boolean).join(', '))}</td>
          <td style="${td}">${esc(BOX_STATUS[b.status].label)}</td></tr>`).join('');
    const totals = summary ? `Vehicles: ${summary.vehicles} &nbsp;|&nbsp; Boxes: ${summary.boxes} (on challan ${summary.used_boxes}, pending ${summary.pending_boxes}) &nbsp;|&nbsp; Items: ${summary.items} (fully ${summary.items_full}, partly ${summary.items_partial}, pending ${summary.items_pending})` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Packing vs Sale Challan</title><style>
      *{margin:0;padding:0;box-sizing:border-box} body{font-family:Arial,sans-serif;padding:20px}
      table{border-collapse:collapse;width:100%} @media print{@page{size:A4 landscape;margin:12mm} body{padding:0}}
    </style></head><body>
      <h2 style="font-size:14pt;margin-bottom:4px;">${esc(settings.company_name || 'Workshop')} — Packing vs Sale Challan (by ${view === 'items' ? 'item' : view === 'boxes' ? 'box' : 'vehicle'})</h2>
      <p style="font-size:9pt;color:#555;margin-bottom:4px;">Date: ${esc(now)}${applied ? ' &nbsp;|&nbsp; ' + esc(applied) : ''}</p>
      <p style="font-size:9pt;color:#333;margin-bottom:10px;">${totals}</p>
      <table><thead><tr>${head.map(h => `<th style="${th}">${h}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    const win = window.open('', '_blank');
    if (!win) return toast.error('Allow pop-ups to export');
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch { /* user closed it */ } }, 500);
  };

  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white';
  const proj = r => [r.proj_code, r.project_name].filter(Boolean).join(' — ') || '—';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <label className="text-xs text-gray-500 font-semibold">Packed from
            <input type="date" value={filters.date_from} onChange={e => set({ date_from: e.target.value })} className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Packed to
            <input type="date" value={filters.date_to} onChange={e => set({ date_to: e.target.value })} className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Challan No.
            <input value={filters.challan} onChange={e => set({ challan: e.target.value })} placeholder="e.g. DC-1001" className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Vehicle No.
            <input value={filters.vehicle} onChange={e => set({ vehicle: e.target.value })} placeholder="e.g. RJ-14-GA-1234" className={inp + ' w-full mt-1'} />
          </label>
          <div className="text-xs text-gray-500 font-semibold">Project
            <div className="mt-1"><ProjectSelect projects={projects} value={filters.project_id} onChange={v => set({ project_id: v })}
              placeholder="All projects" className={inp + ' w-full'} /></div>
          </div>
          <label className="text-xs text-gray-500 font-semibold">Item / Proto / Box
            <input value={filters.item} onChange={e => set({ item: e.target.value })} placeholder="Name, code or box no." className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Status
            <select value={filters.status} onChange={e => set({ status: e.target.value })} disabled={view === 'vehicles'} className={inp + ' w-full mt-1 disabled:opacity-50'}>
              {STATUS_FILTERS[view].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {[['items', 'By Item'], ['boxes', 'By Box'], ['vehicles', 'By Vehicle']].map(([k, l]) => (
              <button key={k} onClick={() => switchView(k)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${view === k ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {hasFilters && (
              <button onClick={() => setFilters(emptyFilters)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2">
                <X className="w-3.5 h-3.5" /> Clear filters
              </button>
            )}
            <button onClick={() => load(filters)} disabled={loading} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm">
              <RefreshCw className={'w-4 h-4' + (loading ? ' animate-spin' : '')} /> Refresh
            </button>
            <button onClick={exportPDF} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-sm font-semibold">
              <FileText className="w-4 h-4" /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { l: 'Boxes on Challan', v: `${summary.used_boxes} / ${summary.boxes}`, s: 'each label (A, B…) is one box', c: 'text-green-700', bg: 'bg-green-50 border-green-200' },
            { l: 'Boxes Pending', v: summary.pending_boxes, s: 'not on a challan yet', c: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
            { l: 'Packed Items', v: summary.items, s: `${summary.items_full} fully · ${summary.items_partial} partly · ${summary.items_pending} pending`, c: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
            { l: 'Sale Challans', v: summary.challans, s: 'using these boxes', c: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
            { l: 'Vehicles', v: summary.vehicles, s: 'carrying these boxes', c: 'text-teal-700', bg: 'bg-teal-50 border-teal-200' },
          ].map(x => (
            <div key={x.l} className={`${x.bg} border rounded-2xl p-3 text-center`}>
              <p className={`text-lg sm:text-xl font-bold ${x.c}`}>{x.v}</p>
              <p className={`text-xs font-semibold mt-0.5 ${x.c}`}>{x.l}</p>
              <p className={`text-[11px] mt-0.5 ${x.c} opacity-75`}>{x.s}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading && !rowsFor(view).length ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-7 h-7 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
        ) : !rowsFor(view).length ? (
          <div className="text-center py-12 text-gray-400 text-sm">{view === 'vehicles' ? 'No boxes are on a challan for these filters' : 'No packing matches these filters'}</div>
        ) : view === 'vehicles' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase">
                  <th className="text-left px-3 py-2.5">Vehicle</th>
                  <th className="text-right px-3 py-2.5">Trips</th>
                  <th className="text-left px-3 py-2.5">Challans</th>
                  <th className="text-right px-3 py-2.5">Boxes</th>
                  <th className="text-left px-3 py-2.5 hidden md:table-cell">Items</th>
                  <th className="text-left px-3 py-2.5 hidden lg:table-cell">Projects</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map(v => (
                  <tr key={v.key} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5 whitespace-nowrap"><Truck className="w-3.5 h-3.5 text-teal-600" />{v.vehicle_number || <span className="text-gray-400 font-normal">No vehicle no.</span>}</div>
                      {v.vehicle_lengths.length > 0 && <div className="text-xs text-gray-600">Length: {v.vehicle_lengths.join(', ')}</div>}
                      {v.transports.length > 0 && <div className="text-[11px] text-gray-400">{v.transports.join(', ')}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{v.trips}</td>
                    <td className="px-3 py-2 text-xs">
                      {v.challans.map(c => (
                        <div key={c.id} className="whitespace-nowrap"><span className="text-teal-700 font-semibold">{c.challan_number}</span>
                          <span className="text-gray-400"> {formatDate(c.challan_date)}{c.client_name ? ` · ${c.client_name}` : ''} · {c.boxes} box{c.boxes === 1 ? '' : 'es'}</span></div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right"><b className="text-gray-900">{v.boxes}</b>{v.labels !== v.boxes && <div className="text-[11px] text-gray-400">{v.labels} labels</div>}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 hidden md:table-cell">{v.items.join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 hidden lg:table-cell">{v.projects.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : view === 'items' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase">
                  <th className="text-left px-3 py-2.5 hidden md:table-cell">Project</th>
                  <th className="text-left px-3 py-2.5">Proto Code</th>
                  <th className="text-left px-3 py-2.5">Item</th>
                  <th className="text-right px-3 py-2.5">Boxes</th>
                  <th className="text-right px-3 py-2.5">On Challan</th>
                  <th className="text-right px-3 py-2.5">Pending</th>
                  <th className="text-right px-3 py-2.5 hidden sm:table-cell">Challan Qty</th>
                  <th className="text-left px-3 py-2.5 hidden lg:table-cell">Challans</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.key} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-3 py-2 text-xs text-gray-600 hidden md:table-cell">{proj(r)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{r.proto_code || <span className="text-gray-400 font-sans">No code</span>}</td>
                    <td className="px-3 py-2">
                      <div className="text-gray-800">{r.item_name || '—'}</div>
                      <div className="text-[11px] text-gray-400">Boxes: {r.box_numbers.join(', ')}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{r.boxes}{r.labels !== r.boxes && <div className="text-[11px] text-gray-400">{r.labels} labels</div>}</td>
                    <td className="px-3 py-2 text-right text-green-700 font-semibold">{r.used_boxes}</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">{r.pending_boxes}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell">{r.challan_qty || '—'}</td>
                    <td className="px-3 py-2 text-xs text-teal-700 hidden lg:table-cell">{r.challans.join(', ') || '—'}</td>
                    <td className="px-3 py-2"><Badge meta={ITEM_STATUS[r.status]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase">
                  <th className="text-left px-3 py-2.5">Box</th>
                  <th className="text-left px-3 py-2.5 hidden md:table-cell">Project</th>
                  <th className="text-left px-3 py-2.5">Proto Code / Item</th>
                  <th className="text-left px-3 py-2.5 hidden lg:table-cell">Contents</th>
                  <th className="text-left px-3 py-2.5 hidden sm:table-cell">Packed On</th>
                  <th className="text-left px-3 py-2.5">Challan</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {boxes.map(b => (
                  <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">{b.box_number}{b.sub_label ? ` (${b.sub_label})` : ''}
                      {b.parent_box_number && <div className="text-[11px] font-normal text-amber-700">label of {b.parent_box_number}</div>}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 hidden md:table-cell">{proj(b)}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs text-gray-700">{b.proto_code || '—'}</div>
                      <div className="text-xs text-gray-500">{b.main_item}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 hidden lg:table-cell">{b.contents.map(c => `${c.item_name} ×${c.quantity}`).join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 hidden sm:table-cell whitespace-nowrap">{formatDate(b.packed_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      {b.challans.length ? b.challans.map(c => (
                        <div key={c.id} className="whitespace-nowrap">
                          <span className="text-teal-700 font-semibold">{c.challan_number}</span> <span className="text-gray-400">{formatDate(c.challan_date)}</span>
                          {vehicleText(c) && <div className="text-[11px] text-gray-600 flex items-center gap-1"><Truck className="w-3 h-3 text-teal-600" />{vehicleText(c)}</div>}
                        </div>
                      )) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2"><Badge meta={BOX_STATUS[b.status]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
